import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rolesService, usersService } from "../../services";
import AdminModalTailwind from "./AdminModalTailwind";
import ConfirmationModal from "./ConfirmationModal";

const formatDateTime = (value, locale = "de-DE") => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

function UsersTailwind() {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formValues, setFormValues] = useState({ username: "", password: "", confirmPassword: "", role: "scorekeeper", venue: "" });
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const queryClient = useQueryClient();

  const rolesQuery = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const res = await rolesService.getRoles();
      if (!res.success) {
        throw new Error(res.error || "Fehler beim Laden der Rollen");
      }
      return res.data || [];
    }
  });

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await usersService.getUsers();
      if (!res.success) {
        throw new Error(res.error || "Fehler beim Laden der Benutzer");
      }
      return res.data || [];
    }
  });

  useEffect(() => {
    if (usersQuery.data) {
      setUsers(usersQuery.data);
    }
  }, [usersQuery.data]);

  useEffect(() => {
    if (editingUser) {
      setFormValues({
        username: editingUser.username,
        password: "",
        confirmPassword: "",
        role: editingUser.role,
        venue: editingUser.venue || ""
      });
    } else {
      setFormValues({
        username: "",
        password: "",
        confirmPassword: "",
        role: "scorekeeper",
        venue: ""
      });
    }
  }, [editingUser, modalOpen]);

  const availableRoles = rolesQuery.data || [];

  const defaultRoleName = useMemo(() => {
    if (!availableRoles.length) return "scorekeeper";
    const nonAdmin = availableRoles.find((role) => role.name !== "admin");
    return nonAdmin?.name || availableRoles[0].name;
  }, [availableRoles]);

  useEffect(() => {
    if (!editingUser && modalOpen) {
      setFormValues((prev) => ({
        ...prev,
        role: defaultRoleName
      }));
    }
  }, [defaultRoleName, editingUser, modalOpen]);

  useEffect(() => {
    if (selectedRole !== "all" && !availableRoles.some((role) => role.name === selectedRole)) {
      setSelectedRole("all");
    }
  }, [availableRoles, selectedRole]);

  const roleTranslations = useMemo(() => {
    const map = {};
    availableRoles.forEach((role) => {
      map[role.name] = t(`roles.${role.name}`, role.name);
    });
    return map;
  }, [availableRoles, t]);

  const roleFilterOptions = useMemo(() => {
    const options = [
      { value: "all", label: t("admin.users.filters.all", "Alle") }
    ];

    availableRoles.forEach((role) => {
      options.push({
        value: role.name,
        label: roleTranslations[role.name] || t(`roles.${role.name}`, role.name)
      });
    });

    return options;
  }, [availableRoles, roleTranslations, t]);

  const getRoleCardConfig = (roleName) => {
    switch (roleName) {
      case "admin":
        return { icon: "fas fa-user-shield", bg: "bg-green-100 dark:bg-green-900/30", iconColor: "text-green-700 dark:text-green-300" };
      case "scorekeeper":
        return { icon: "fas fa-clipboard-list", bg: "bg-blue-100 dark:bg-blue-900/30", iconColor: "text-blue-700 dark:text-blue-300" };
      default:
        return { icon: "fas fa-user-tag", bg: "bg-slate-100 dark:bg-slate-700", iconColor: "text-slate-600 dark:text-slate-300" };
    }
  };

  const getRoleBadgeClasses = (roleName) => {
    switch (roleName) {
      case "admin":
        return "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300";
      case "scorekeeper":
        return "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300";
      default:
        return "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300";
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRoleFilterChange = (role) => {
    if (role !== "all" && !availableRoles.some((item) => item.name === role)) {
      setSelectedRole("all");
      return;
    }
    setSelectedRole(role);
  };

  const addToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: "info", message: "" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, type: "info", message: "" });
  };

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = selectedRole === "all" || user.role === selectedRole;
      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, selectedRole]);

  const roleCounts = useMemo(() => {
    const counts = {};
    users.forEach((user) => {
      counts[user.role] = (counts[user.role] || 0) + 1;
    });
    return counts;
  }, [users]);

  const usersCount = users.length;

  const createUserMutation = useMutation({
    mutationFn: usersService.createUser.bind(usersService),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      setModalOpen(false);
      addToast("success", t("admin.users.toast.created", "Benutzer erfolgreich angelegt"));
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.users.toast.createError", "Fehler beim Anlegen des Benutzers"));
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, payload }) => usersService.updateUser(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["users"]);
      setModalOpen(false);
      addToast("success", t("admin.users.toast.updated", "Benutzer erfolgreich aktualisiert"));
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.users.toast.updateError", "Fehler beim Aktualisieren des Benutzers"));
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: usersService.deleteUser.bind(usersService),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      addToast("success", t("admin.users.toast.deleted", "Benutzer erfolgreich gelöscht"));
      setDeleteModalOpen(false);
      setUserToDelete(null);
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.users.toast.deleteError", "Fehler beim Löschen des Benutzers"));
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }) => usersService.resetPassword(id, password),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["users"]);
      setResetModalOpen(false);
      setResetUser(null);
      setResetPassword("");
      setResetPasswordConfirm("");
      addToast("success", t("admin.users.toast.reset", "Passwort wurde zurückgesetzt"));
    },
    onError: (error) => {
      addToast("error", error.message || t("admin.users.toast.resetError", "Fehler beim Zurücksetzen des Passworts"));
    }
  });

  const openCreateModal = () => {
    setEditingUser(null);
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    setFormValues({ username: "", password: "", confirmPassword: "", role: "scorekeeper", venue: "" });
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveUser = () => {
    if (!formValues.username || !formValues.username.trim()) {
      addToast("warning", t("admin.users.validation.usernameRequired", "Benutzername darf nicht leer sein."));
      return;
    }

    const roleIsValid =
      availableRoles.length === 0 ||
      availableRoles.some((role) => role.name === formValues.role);

    if (!roleIsValid) {
      addToast("warning", t("admin.users.validation.roleInvalid", "Bitte wähle eine gültige Rolle."));
      return;
    }

    if (editingUser) {
      const payload = {
        username: formValues.username.trim(),
        role: formValues.role
      };

      if (formValues.password) {
        if (formValues.password.length < 8) {
          addToast("warning", t("admin.users.validation.passwordMin", "Passwort muss mindestens 8 Zeichen lang sein."));
          return;
        }
        if (formValues.password !== formValues.confirmPassword) {
          addToast("warning", t("admin.users.validation.passwordMismatch", "Passwörter stimmen nicht überein."));
          return;
        }
        payload.password = formValues.password;
      }

      // Venue für Scorekeeper und Caller setzen
      if (formValues.role === "scorekeeper" || formValues.role === "caller") {
        payload.venue = formValues.venue || null;
      } else {
        payload.venue = null;
      }

      updateUserMutation.mutate({
        id: editingUser.id,
        payload
      });
    } else {
      if (!formValues.password || formValues.password.length < 8) {
        addToast("warning", t("admin.users.validation.passwordMin", "Passwort muss mindestens 8 Zeichen lang sein."));
        return;
      }
      if (formValues.password !== formValues.confirmPassword) {
        addToast("warning", t("admin.users.validation.passwordMismatch", "Passwörter stimmen nicht überein."));
        return;
      }
      const createPayload = {
        username: formValues.username.trim(),
        password: formValues.password,
        role: formValues.role
      };
      
      // Venue für Scorekeeper und Caller setzen
      if (formValues.role === "scorekeeper" || formValues.role === "caller") {
        createPayload.venue = formValues.venue || null;
      }
      
      createUserMutation.mutate(createPayload);
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const openResetModal = (user) => {
    setResetUser(user);
    setResetPassword("");
    setResetPasswordConfirm("");
    setResetModalOpen(true);
  };

  const handleResetPassword = () => {
    if (!resetPassword || resetPassword.length < 8) {
      addToast("warning", t("admin.users.validation.passwordMin", "Passwort muss mindestens 8 Zeichen lang sein."));
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      addToast("warning", t("admin.users.validation.passwordMismatch", "Passwörter stimmen nicht überein."));
      return;
    }
    resetPasswordMutation.mutate({
      id: resetUser.id,
      password: resetPassword
    });
  };

  const closeResetModal = () => {
    if (resetPasswordMutation.isLoading) return;
    setResetModalOpen(false);
    setResetUser(null);
    setResetPassword("");
    setResetPasswordConfirm("");
  };

  if (usersQuery.isLoading || rolesQuery.isLoading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64 mb-2"></div>
            <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              <div className="flex gap-2">
                <div className="w-24 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
              </div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-3"></div>
            <div className="flex flex-wrap gap-1.5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
              ))}
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
                  <tr>
                    {[...Array(7)].map((_, i) => (
                      <th key={i} className="px-6 py-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {[...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(7)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (usersQuery.isError || rolesQuery.isError) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
              </div>
              <p className="text-slate-600 dark:text-slate-400 font-medium mb-4">
                {usersQuery.error?.message || rolesQuery.error?.message || t("admin.users.error.load", "Fehler beim Laden der Benutzer.")}
              </p>
              <button
                onClick={() => {
                  usersQuery.refetch();
                  rolesQuery.refetch();
                }}
                className="bg-primary dark:bg-primary-light hover:bg-primary-dark dark:hover:bg-primary text-white px-6 py-3 rounded-md transition-colors duration-200 font-medium"
              >
                {t("admin.retry", "Erneut versuchen")}
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {t("admin.users.title", "Benutzerverwaltung")}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                {t("admin.users.subtitle", "Verwalte Admins und Punktschreiber, passe Rollen und Zugänge an.")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{filteredUsers.length}</div>
                <div className="text-sm text-slate-500">
                  {t("admin.users.meta.visible", "Sichtbare Benutzer")}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={t("admin.users.search.placeholder", "Benutzername suchen...")}
                className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm" />
            </div>
            {/* Actions */}
            <div className="flex gap-2 items-stretch">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark dark:bg-primary-light dark:hover:bg-primary text-white px-4 h-[46px] rounded-md text-sm font-medium transition-colors"
              >
                <i className="fas fa-user-plus text-xs" />
                {t("admin.users.actions.add", "Benutzer hinzufügen")}
              </button>
            </div>
          </div>
        </div>

        {/* Filter Groups */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {t("admin.users.filters.title", "Rolle")}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {roleFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleRoleFilterChange(option.value)}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      selectedRole === option.value
                        ? "bg-primary dark:bg-primary-light text-white"
                        : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                <i className="fas fa-users text-slate-600 dark:text-slate-400 text-xl" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{usersCount}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {t("admin.users.stats.total", "Gesamte Benutzer")}
                </div>
              </div>
            </div>
          </div>
          {availableRoles.map((role) => {
            const config = getRoleCardConfig(role.name);
            return (
              <div key={role.id || role.name} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${config.bg}`}>
                    <i className={`${config.icon} ${config.iconColor} text-xl`} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{roleCounts[role.name] || 0}</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {roleTranslations[role.name] || t(`roles.${role.name}`, role.name)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.users.table.id", "ID")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.users.table.username", "Benutzername")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.users.table.role", "Rolle")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.users.table.venue", "Fläche")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.users.table.createdAt", "Erstellt am")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.users.table.updatedAt", "Aktualisiert am")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t("admin.users.table.actions", "Aktionen")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                {filteredUsers.map((user, index) => (
                  <tr key={user.id} className={`transition-colors ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-700/30'} hover:bg-slate-50 dark:hover:bg-slate-700/50`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500 dark:text-slate-400">
                      {user.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {user.username}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeClasses(user.role)}`}
                      >
                        {roleTranslations[user.role] || t(`roles.${user.role}`, user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.role === "scorekeeper" || user.role === "caller" ? (
                        user.venue ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                            {user.venue}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 dark:text-slate-500 italic">
                            {t("admin.users.table.venueAll", "Beide")}
                          </span>
                        )
                      ) : (
                        <span className="text-xs text-slate-400 dark:text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                      {formatDateTime(user.createdAt, i18n.language)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400">
                      {formatDateTime(user.updatedAt, i18n.language)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                          title={t("admin.users.actions.edit", "Bearbeiten")}
                        >
                          <i className="fas fa-edit text-sm" />
                        </button>
                        <button
                          onClick={() => openResetModal(user)}
                          className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                          title={t("admin.users.actions.resetPassword", "Passwort zurücksetzen")}
                          disabled={resetPasswordMutation.isLoading && resetUser?.id === user.id}
                        >
                          <i className={`fas ${resetPasswordMutation.isLoading && resetUser?.id === user.id ? 'fa-spinner fa-spin' : 'fa-key'} text-sm`} />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                          title={t("admin.users.actions.delete", "Löschen")}
                          disabled={deleteUserMutation.isLoading}
                        >
                          <i className="fas fa-trash text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty state */}
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-user-slash text-slate-400 dark:text-slate-500 text-2xl" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
              {t("admin.users.empty.title", "Keine Benutzer gefunden")}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {searchTerm || selectedRole !== "all"
                ? t("admin.users.empty.filter", "Ändere die Filter oder den Suchbegriff.")
                : t("admin.users.empty.action", "Lege den ersten Benutzer an, um zu starten.")}
            </p>
            {(!searchTerm && selectedRole === "all") && (
              <button
                onClick={openCreateModal}
                className="bg-slate-900 dark:bg-slate-700 hover:bg-slate-800 dark:hover:bg-slate-600 text-white dark:text-slate-100 px-6 py-3 rounded-md transition-colors font-medium"
              >
                <i className="fas fa-user-plus mr-2" />
                {t("admin.users.actions.addFirst", "Ersten Benutzer hinzufügen")}
              </button>
            )}
          </div>
        )}

        {/* Modal */}
        <AdminModalTailwind
          isOpen={modalOpen}
          onClose={closeModal}
          title={
            editingUser
              ? t("admin.users.modal.editTitle", "Benutzer bearbeiten")
              : t("admin.users.modal.createTitle", "Benutzer hinzufügen")
          }
          subtitle={
            editingUser
              ? t("admin.users.modal.editSubtitle", "Passe Benutzername, Passwort oder Rolle an.")
              : t("admin.users.modal.createSubtitle", "Lege einen neuen Benutzer mit Rolle fest.")
          }
          onSave={handleSaveUser}
          onCancel={closeModal}
          saveButtonText={
            editingUser
              ? t("admin.users.modal.save", "Änderungen speichern")
              : t("admin.users.modal.create", "Benutzer erstellen")
          }
          cancelButtonText={t("admin.cancel", "Abbrechen")}
          isSaving={createUserMutation.isLoading || updateUserMutation.isLoading}
        >
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t("admin.users.form.username", "Benutzername")}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                value={formValues.username}
                onChange={handleFormChange}
                placeholder={t("admin.users.form.usernamePlaceholder", "z.B. scorekeeper-01")}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t("admin.users.form.password", "Passwort")}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formValues.password}
                onChange={handleFormChange}
                placeholder={
                  editingUser
                    ? t("admin.users.form.passwordPlaceholderOptional", "Leer lassen, um das Passwort unverändert zu lassen")
                    : t("admin.users.form.passwordPlaceholder", "Mindestens 8 Zeichen")
                }
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
                required={!editingUser}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t("admin.users.form.confirmPassword", "Passwort bestätigen")}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formValues.confirmPassword}
                onChange={handleFormChange}
                placeholder={t("admin.users.form.confirmPasswordPlaceholder", "Passwort erneut eingeben")}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
                required={!editingUser}
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t("admin.users.form.role", "Rolle")}
              </label>
              <select
                id="role"
                name="role"
                value={formValues.role}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              >
                {availableRoles.length === 0
                  ? (
                    <option value="scorekeeper">{t("roles.scorekeeper", "Punktschreiber")}</option>
                  )
                  : availableRoles.map((role) => (
                      <option key={role.id || role.name} value={role.name}>
                        {roleTranslations[role.name] || t(`roles.${role.name}`, role.name)}
                      </option>
                    ))}
              </select>
            </div>

            {(formValues.role === "scorekeeper" || formValues.role === "caller") && (
              <div>
                <label htmlFor="venue" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t("admin.users.form.venue", "Zugewiesene Fläche")}
                </label>
                <select
                  id="venue"
                  name="venue"
                  value={formValues.venue}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                >
                  <option value="">{t("admin.users.form.venueAll", "Beide Flächen (keine Zuweisung)")}</option>
                  <option value="Fläche A">{t("admin.users.form.venueA", "Fläche A")}</option>
                  <option value="Fläche B">{t("admin.users.form.venueB", "Fläche B")}</option>
                </select>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {formValues.role === "scorekeeper" 
                    ? t("admin.users.form.venueHint", "Wenn keine Fläche zugewiesen ist, kann der Scorekeeper sich eine Fläche auswählen.")
                    : t("admin.users.form.venueHintCaller", "Wenn keine Fläche zugewiesen ist, kann der Caller sich eine Fläche auswählen.")
                  }
                </p>
              </div>
            )}
          </div>
        </AdminModalTailwind>

        <AdminModalTailwind
          isOpen={resetModalOpen}
          onClose={closeResetModal}
          title={t("admin.users.reset.title", "Passwort zurücksetzen")}
          subtitle={
            resetUser
              ? t("admin.users.reset.subtitle", 'Neues Passwort für "{{username}}" festlegen.', {
                  username: resetUser.username
                })
              : ""
          }
          onSave={handleResetPassword}
          onCancel={closeResetModal}
          saveButtonText={t("admin.users.reset.save", "Passwort setzen")}
          cancelButtonText={t("admin.cancel", "Abbrechen")}
          isSaving={resetPasswordMutation.isLoading}
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="reset-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t("admin.users.reset.newPassword", "Neues Passwort")}
              </label>
              <input
                id="reset-password"
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder={t("admin.users.form.passwordPlaceholder", "Mindestens 8 Zeichen")}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
                required
              />
            </div>
            <div>
              <label htmlFor="reset-password-confirm" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t("admin.users.reset.confirmPassword", "Passwort bestätigen")}
              </label>
              <input
                id="reset-password-confirm"
                type="password"
                value={resetPasswordConfirm}
                onChange={(e) => setResetPasswordConfirm(e.target.value)}
                placeholder={t("admin.users.form.confirmPasswordPlaceholder", "Passwort erneut eingeben")}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
                required
              />
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("admin.users.reset.hint", "Das neue Passwort wird unmittelbar aktiv. Teile es dem Nutzer sicher mit.")}
            </p>
          </div>
        </AdminModalTailwind>

        <ConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => {
            if (deleteUserMutation.isLoading) return;
            setDeleteModalOpen(false);
            setUserToDelete(null);
          }}
          onConfirm={() => {
            if (userToDelete && !deleteUserMutation.isLoading) {
              deleteUserMutation.mutate(userToDelete.id);
            }
          }}
          title={t("admin.users.delete.title", "Benutzer löschen")}
          subtitle={t("admin.users.delete.warning", "Diese Aktion kann nicht rückgängig gemacht werden.")}
          message={
            userToDelete
              ? t("admin.users.delete.subtitle", 'Soll der Benutzer "{{username}}" wirklich gelöscht werden? Der Benutzer verliert sofort den Zugang zum System.', {
                  username: userToDelete.username
                })
              : ""
          }
          confirmText={t("admin.users.delete.confirm", "Löschen")}
          cancelText={t("admin.cancel", "Abbrechen")}
          type="danger"
          layout="inline"
          isLoading={deleteUserMutation.isLoading}
        />

        {/* Toast */}
        {toast.show && (
          <div className="fixed top-4 right-4 z-50">
            <div
              className={`px-4 py-3 rounded-lg border shadow-lg bg-white dark:bg-slate-800 ${
                toast.type === "success"
                  ? "border-green-200 dark:border-green-800"
                  : toast.type === "error"
                  ? "border-red-200 dark:border-red-800"
                  : toast.type === "warning"
                  ? "border-yellow-200 dark:border-yellow-800"
                  : "border-blue-200 dark:border-blue-800"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    toast.type === "success"
                      ? "bg-green-100 dark:bg-green-900/30"
                      : toast.type === "error"
                      ? "bg-red-100 dark:bg-red-900/30"
                      : toast.type === "warning"
                      ? "bg-yellow-100 dark:bg-yellow-900/30"
                      : "bg-blue-100 dark:bg-blue-900/30"
                  }`}
                >
                  <i
                    className={`fas text-xs ${
                      toast.type === "success"
                        ? "fa-check text-green-600 dark:text-green-400"
                        : toast.type === "error"
                        ? "fa-times text-red-600 dark:text-red-400"
                        : toast.type === "warning"
                        ? "fa-exclamation-triangle text-yellow-600 dark:text-yellow-400"
                        : "fa-info text-blue-600 dark:text-blue-400"
                    }`}
                  />
                </div>
                <span
                  className={`text-sm font-medium ${
                    toast.type === "success"
                      ? "text-green-900 dark:text-green-100"
                      : toast.type === "error"
                      ? "text-red-900 dark:text-red-100"
                      : toast.type === "warning"
                      ? "text-yellow-900 dark:text-yellow-100"
                      : "text-blue-900 dark:text-blue-100"
                  }`}
                >
                  {toast.message}
                </span>
                <button
                  onClick={hideToast}
                  className="ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  <i className="fas fa-times text-xs" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

export default UsersTailwind;

